import { useEffect, useState, type ReactNode } from 'react'
import type { Dayjs } from 'dayjs'
import { useNavigate } from 'react-router-dom'
import {
  CheckOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  EyeOutlined,
  SearchOutlined,
  SyncOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import {
  Button,
  Empty,
  Flex,
  FloatButton,
  InputNumber,
  Popconfirm,
  Popover,
  Select,
  Skeleton,
  Space,
  Tooltip,
} from 'antd'
import { ROLES, STATUS_OPTIONS, TODOS } from '@constants'
import { useGlobalFilial, useGlobalUser } from '@hooks'
import { DefaultDateTimeInput, DefaultTable } from 'diferpan-common-web'
import { useAppMessage } from '@providers'
import {
  useAceitarNovaDataAgendamentoMutation,
  useAgendamentoDetalhesQuery,
  useAgendamentoNotasQuery,
  useAgendamentosFornecedorQuery,
  useFiliaisQuery,
  useRejeitarNovaDataAgendamentoMutation,
} from '@queries'
import { formatDateTime, resolverMensagemErro } from '@utils'
import dayjs from 'dayjs'
import './style.scss'
import type {
  AgendaRecebimentoBookingNFResponse,
  AgendaRecebimentoBookingDetailsResponse,
  AgendaRecebimentoReceivingBookingSupplierResponse,
  Identificador,
} from '../../types'

type AgendaFornecedor = AgendaRecebimentoReceivingBookingSupplierResponse & {
  key: number
  status: string
  dataEntrega: string
  dataSolicitacao: string
  totalVolumes: number
}

interface ColunaTabela<T> {
  title: string
  dataIndex?: string
  key: string
  width?: number
  fixed?: 'left' | 'right'
  align?: 'left' | 'center' | 'right'
  className?: string
  searchable?: boolean
  sortDirections?: ('ascend' | 'descend')[]
  sorter?: (primeiro: T, segundo: T) => number
  render?: (valor: unknown, registro: T) => ReactNode
}

interface DescricaoMotivoStatus {
  rotulo: string
  texto: string
  icone: ReactNode
  isAviso: boolean
}

interface MotivoStatusProps {
  record: AgendaFornecedor
  isExpandido: boolean
  onToggle: (chaveAgenda: number) => void
}

interface PainelPesquisaNotaFiscalProps {
  valor: number | null
  onChange: (valor: number | null) => void
  onBuscar: () => void
  isFetching: boolean
  notas: AgendaRecebimentoBookingNFResponse[]
  hasPesquisa: boolean
  onFechar: () => void
  onDetalhes: (record: AgendaRecebimentoBookingNFResponse) => void
}

interface DetalhesAgendamentoExpandidoProps {
  record: AgendaFornecedor
  onNovaDataProcessada: () => void
}

const LIMITE_CARACTERES_MOTIVO = 90

const STATUS_COM_PASTILHA = ['AGENDADO', 'ALTERADO', 'CANCELADO', 'ENTREGUE', 'NOVO', 'REJEITADO']

const ID_PAINEL_PESQUISA_NF = 'painel-pesquisa-nf-supplier'

const FORMATO_DATA_ENTREGA = 'YYYY-MM-DD HH:mm:ss'

const formatadorDeNumero = new Intl.NumberFormat('pt-BR')

const idRegiaoDetalhes = (chaveAgenda: number) => `detalhe-agendamento-supplier-${chaveAgenda}`

const resolverMotivoStatus = (record: AgendaFornecedor): DescricaoMotivoStatus | null => {
  if (record.status === 'CANCELADO' && record.motivoCancelamento) {
    return {
      rotulo: 'Motivo do cancelamento',
      texto: record.motivoCancelamento,
      icone: <CloseCircleOutlined />,
      isAviso: false,
    }
  }

  if (record.status === 'REJEITADO' && record.motivoRejeicao) {
    return {
      rotulo: 'Motivo da rejeição',
      texto: record.motivoRejeicao,
      icone: <UndoOutlined />,
      isAviso: false,
    }
  }

  if (record.status === 'ALTERADO' && record.motivoRejeicao) {
    return {
      rotulo: 'Motivo da alteração',
      texto: record.motivoRejeicao,
      icone: <SyncOutlined />,
      isAviso: true,
    }
  }

  return null
}

const renderPastilhaStatus = (status: string | null) => {
  if (status === null) return null

  const hasPastilhaSemantica = STATUS_COM_PASTILHA.includes(status)
  const classesPastilha = [
    'status-cell-supplier-report-class__pastilha',
    hasPastilhaSemantica ? `status-cell-supplier-report-class__pastilha--${status.toLowerCase()}` : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={classesPastilha}>
      <span className={'status-cell-supplier-report-class__ponto'} aria-hidden={true} />
      {status}
    </span>
  )
}

const columnsBookingDetails: ColunaTabela<AgendaRecebimentoBookingDetailsResponse>[] = [
  {
    title: 'Id',
    dataIndex: 'key',
    key: 'key',
    width: 150,
    sorter: (a, b) => a.key - b.key,
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Agenda ID',
    dataIndex: 'agendaId',
    key: 'agendaId',
    width: 110,
    sorter: (a, b) => a.agendaId - b.agendaId,
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 250,
    sorter: (a, b) => a.status.localeCompare(b.status),
    sortDirections: ['descend', 'ascend'],
    searchable: true,
    render: (valor) => renderPastilhaStatus(String(valor)),
  },
  {
    title: 'Números da NF',
    dataIndex: 'numeroNotas',
    key: 'numeroNotas',
    width: 150,
    className: 'table-supplier-report-class__numero',
    sorter: (a, b) => a.numeroNotas.localeCompare(b.numeroNotas),
    sortDirections: ['descend', 'ascend'],
    searchable: true,
  },
  {
    title: 'Número do Conhecimento',
    dataIndex: 'numeroCfrete',
    key: 'numeroCfrete',
    width: 150,
    className: 'table-supplier-report-class__numero',
    sorter: (a, b) => a.numeroCfrete - b.numeroCfrete,
    sortDirections: ['descend', 'ascend'],
  },
  {
    title: 'Tipo CTE',
    dataIndex: 'tipoCte',
    key: 'tipoCte',
    width: 150,
    sorter: (a, b) => a.tipoCte.localeCompare(b.tipoCte),
    sortDirections: ['descend', 'ascend'],
  },
]

const MotivoStatus = ({ record, isExpandido, onToggle }: MotivoStatusProps) => {
  const motivo = resolverMotivoStatus(record)
  if (motivo === null) return null

  const idBlocoMotivo = `motivo-status-supplier-${record.key}`
  const hasMotivoLongo = motivo.texto.length > LIMITE_CARACTERES_MOTIVO
  const classesMotivo = [
    'motivo-status-supplier-report-class',
    motivo.isAviso ? 'motivo-status-supplier-report-class--aviso' : '',
    isExpandido ? 'motivo-status-supplier-report-class--expandido' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <span className={classesMotivo} id={idBlocoMotivo}>
      <span className={'motivo-status-supplier-report-class__rotulo'}>
        {motivo.icone}
        {motivo.rotulo}
      </span>
      <span className={'motivo-status-supplier-report-class__texto'}>{motivo.texto}</span>
      {hasMotivoLongo && (
        <Button
          type='link'
          size='small'
          className={'motivo-status-supplier-report-class__acao'}
          aria-expanded={isExpandido}
          aria-controls={idBlocoMotivo}
          onClick={() => onToggle(record.key)}
        >
          {isExpandido ? 'Recolher motivo' : 'Ver motivo completo'}
        </Button>
      )}
    </span>
  )
}

const PainelPesquisaNotaFiscal = ({
  valor,
  onChange,
  onBuscar,
  isFetching,
  notas,
  hasPesquisa,
  onFechar,
  onDetalhes,
}: PainelPesquisaNotaFiscalProps) => {
  const columnsBookingNotaFiscal: ColunaTabela<AgendaRecebimentoBookingNFResponse>[] = [
    {
      title: 'Agenda ID',
      dataIndex: 'agendaId',
      key: 'agendaId',
      width: 110,
      sorter: (a, b) => a.agendaId - b.agendaId,
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Fornecedores',
      dataIndex: 'fornecedores',
      key: 'fornecedores',
      width: 250,
      sorter: (a, b) => a.fornecedores.localeCompare(b.fornecedores),
      sortDirections: ['descend', 'ascend'],
      searchable: true,
    },
    {
      title: 'Detalhes',
      key: 'detalhes',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type='dashed'
          size='small'
          icon={<EyeOutlined />}
          className={'botao-supplier-report-class'}
          aria-label={`Detalhes do Agendamento ${record.agendaId}`}
          onClick={() => onDetalhes(record)}
        >
          Detalhes
        </Button>
      ),
    },
  ]

  return (
    <div className={'nf-panel-supplier-report-class__conteudo'} id={ID_PAINEL_PESQUISA_NF}>
      <div className={'nf-panel-supplier-report-class__cabecalho'}>
        <span className={'nf-panel-supplier-report-class__titulo'}>Pesquisar por NF</span>
        <Button
          type='text'
          size='small'
          icon={<CloseOutlined />}
          className={'botao-supplier-report-class'}
          aria-label='Fechar pesquisa por NF'
          onClick={onFechar}
        />
      </div>
      <div className={'filter-field-supplier-report-class'}>
        <label htmlFor='numero-nf-supplier' className={'filter-field-supplier-report-class__rotulo'}>
          Número da NF
        </label>
        <Space.Compact block={true}>
          <InputNumber
            id='numero-nf-supplier'
            placeholder='Numero da NF'
            min={0}
            className={'nf-panel-supplier-report-class__entrada'}
            value={valor}
            onChange={onChange}
          />
          <Tooltip title='Pesquisa automática pela chave de acesso'>
            <Button
              type='primary'
              icon={<SearchOutlined />}
              className={'botao-supplier-report-class'}
              aria-label='Buscar pelo número da NF'
              loading={isFetching}
              onClick={onBuscar}
            />
          </Tooltip>
        </Space.Compact>
      </div>
      <div className={'nf-panel-supplier-report-class__resultado'}>
        <Skeleton loading={isFetching} active paragraph={{ rows: 3 }}>
          {notas.length === 0 ? (
            <Empty
              description={
                hasPesquisa
                  ? 'Nenhum agendamento encontrado para esta nota fiscal'
                  : 'Informe o número da NF para localizar a agenda correspondente.'
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <DefaultTable
              baseColumns={columnsBookingNotaFiscal}
              dataSource={notas}
              scroll={{ x: 'max-content' }}
              className={'table-supplier-report-booking-notas'}
            />
          )}
        </Skeleton>
      </div>
    </div>
  )
}

const DetalhesAgendamentoExpandido = ({ record, onNovaDataProcessada }: DetalhesAgendamentoExpandidoProps) => {
  const message = useAppMessage()
  const [novaDataSugerida, setNovaDataSugerida] = useState<Dayjs | null>(() =>
    dayjs(record.dataEntrega, FORMATO_DATA_ENTREGA)
  )

  const detalhesQuery = useAgendamentoDetalhesQuery({ agendaId: record.key, aberto: true })
  const aceitarMutation = useAceitarNovaDataAgendamentoMutation()
  const rejeitarMutation = useRejeitarNovaDataAgendamentoMutation()

  useEffect(() => {
    if (detalhesQuery.error)
      message.error(resolverMensagemErro(detalhesQuery.error, 'Erro ao buscar os detalhes do Agendamento!'))
  }, [detalhesQuery.error])

  const dataEntregaFormatada = formatDateTime(record.dataEntrega)
  const isStatusPendenteDeAceite = record.status === 'PENDENTE DE ACEITE'
  const hasNovaDataParaValidar = record.status === 'ALTERADO'
  const isNovaDataMantida =
    novaDataSugerida !== null && dayjs(record.dataEntrega, FORMATO_DATA_ENTREGA).isSame(novaDataSugerida, 'minute')
  const isProcessandoNovaData = aceitarMutation.isPending || rejeitarMutation.isPending

  const handleNovaDataChange = (data: Dayjs | null) => {
    setNovaDataSugerida(data)
  }

  const handleValidarNovaDataClick = async () => {
    if (novaDataSugerida === null) return

    if (isNovaDataMantida) {
      try {
        await aceitarMutation.mutateAsync({ agendaId: record.key })
        message.success('Agendamento finalizado!!')
      } catch (error) {
        message.error(resolverMensagemErro(error, 'Erro ao aceitar o agendamento.'))
      }
    } else {
      try {
        await rejeitarMutation.mutateAsync({
          agendaId: record.key,
          newDate: novaDataSugerida.format('YYYY-MM-DDTHH:mm:ss'),
        })
        message.success('Nova data sugerida com Sucesso!')
      } catch (error) {
        message.error(resolverMensagemErro(error, 'Erro ao rejeitar o agendamento.'))
      }
    }
    onNovaDataProcessada()
  }

  return (
    <div
      className={'row-detail-supplier-report-class'}
      id={idRegiaoDetalhes(record.key)}
      role='region'
      aria-label={`Detalhes do agendamento ${record.key}`}
    >
      <div className={'row-detail-supplier-report-class__cabecalho'}>
        <span className={'row-detail-supplier-report-class__titulo'}>Detalhes do agendamento {record.key}</span>
      </div>
      <dl className={'detail-panel-supplier-report-class'}>
        <div className={'detail-panel-supplier-report-class__par'}>
          <dt>ID Agenda</dt>
          <dd>{record.key}</dd>
        </div>
        <div className={'detail-panel-supplier-report-class__par'}>
          <dt>Status</dt>
          <dd>{renderPastilhaStatus(record.status)}</dd>
        </div>
        {!isStatusPendenteDeAceite && (
          <div className={'detail-panel-supplier-report-class__par'}>
            <dt>{hasNovaDataParaValidar ? 'Nova data sugerida' : 'Data Previsão'}</dt>
            <dd className={hasNovaDataParaValidar ? 'detail-panel-supplier-report-class__valor--aviso' : undefined}>
              {dataEntregaFormatada}
            </dd>
          </div>
        )}
        <div className={'detail-panel-supplier-report-class__par'}>
          <dt>Dia Solicitado</dt>
          <dd>{formatDateTime(record.dataSolicitacao)}</dd>
        </div>
        <div className={'detail-panel-supplier-report-class__par'}>
          <dt>Tipo de Caminhão</dt>
          <dd>{record.tipoCaminhoes ?? '—'}</dd>
        </div>
        <div className={'detail-panel-supplier-report-class__par'}>
          <dt>Volumes Solicitados</dt>
          <dd className={'table-supplier-report-class__numero'}>
            {formatadorDeNumero.format(record.totalVolumes ?? 0)}
          </dd>
        </div>
      </dl>
      <Skeleton loading={detalhesQuery.isFetching} active paragraph={{ rows: 5 }}>
        <DefaultTable
          baseColumns={columnsBookingDetails}
          dataSource={detalhesQuery.data ?? []}
          fileName={`notaFiscal-Agenda-${record.key}.xlsx`}
          className={'table-supplier-report-booking-details'}
          scroll={{ x: 'max-content' }}
        />
      </Skeleton>
      {hasNovaDataParaValidar && (
        <div className={'row-detail-supplier-report-class__cabecalho'}>
          <span className={'row-detail-supplier-report-class__titulo'}>Nova data sugerida pelo recebimento</span>
          <div className={'row-detail-supplier-report-class__acoes'}>
            <DefaultDateTimeInput
              placeholder='Alterar data Sugerida'
              value={novaDataSugerida}
              onChange={handleNovaDataChange}
              enableBusinessDays={true}
              width='260px'
              height='34px'
            />
            <Popconfirm
              title={
                isNovaDataMantida
                  ? 'Tem certeza de que deseja ACEITAR a nova data?'
                  : 'Tem certeza de que deseja REJEITAR e ALTERAR a data?'
              }
              onConfirm={() => handleValidarNovaDataClick()}
              okText='Sim'
              cancelText='Não'
            >
              <Button
                type='primary'
                icon={<CheckOutlined />}
                loading={isProcessandoNovaData}
                className={'botao-supplier-report-class'}
                aria-label={`Validar a nova data de entrega ${dataEntregaFormatada} do agendamento ${record.key}`}
              >
                Validar nova data
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}
    </div>
  )
}

export const RelatorioFornecedor = () => {
  const navigate = useNavigate()
  const message = useAppMessage()
  const [selectedFilial, setSelectedFilial] = useState('1')
  const [user] = useGlobalUser()
  const [, definirFilialGlobal] = useGlobalFilial()
  const [selectedStatus, setSelectedStatus] = useState(TODOS)
  const [agendaExpandida, setAgendaExpandida] = useState<number | null>(null)
  const [openBookingSearch, setOpenBookingSearch] = useState(false)
  const [numberNotaFiscal, setNumberNotaFiscal] = useState<number | null>(null)
  const [notaFiscalPesquisada, setNotaFiscalPesquisada] = useState<Identificador | null>(null)
  const [motivosExpandidos, setMotivosExpandidos] = useState<number[]>([])
  const hasRolePreAgendamento = user.userRoles.some((role) => ROLES.PRE_AGENDAMENTO.includes(role))

  const filiaisQuery = useFiliaisQuery()
  const agendamentosQuery = useAgendamentosFornecedorQuery({
    codigoUsuario: user.code,
    filial: selectedFilial,
    status: selectedStatus,
  })
  const notasQuery = useAgendamentoNotasQuery({ numeroNotaFiscal: notaFiscalPesquisada, codigoUsuario: user.code })

  useEffect(() => {
    if (filiaisQuery.error) message.error(resolverMensagemErro(filiaisQuery.error, 'Erro ao buscar as Filiais!!'))
  }, [filiaisQuery.error])

  useEffect(() => {
    if (agendamentosQuery.error)
      message.error(resolverMensagemErro(agendamentosQuery.error, 'Erro ao buscar os Agendamentos'))
  }, [agendamentosQuery.error])

  useEffect(() => {
    if (notasQuery.error) message.error(resolverMensagemErro(notasQuery.error, 'Erro ao buscar os dados da Agenda!'))
  }, [notasQuery.error])

  const agendamentos = agendamentosQuery.data ?? []
  const totalVolumesFiltrados = agendamentos.reduce((total, agenda) => total + (agenda.totalVolumes ?? 0), 0)
  const hasPesquisaPorNotaFiscal = notaFiscalPesquisada !== null
  const notasEncontradas = notasQuery.data ?? []
  const rotuloFilialSelecionada =
    (filiaisQuery.data ?? []).find((filial) => String(filial.value) === selectedFilial)?.label ??
    `Filial ${selectedFilial}`
  const rotuloStatusSelecionado =
    STATUS_OPTIONS.find((opcao) => opcao.value === selectedStatus)?.label ?? selectedStatus
  const horarioUltimaAtualizacao = agendamentosQuery.dataUpdatedAt
    ? dayjs(agendamentosQuery.dataUpdatedAt).format('HH:mm')
    : null
  const isAtualizandoAgendamentos = agendamentosQuery.isFetching && !agendamentosQuery.isPending
  const chavesExpandidas = agendaExpandida === null ? [] : [agendaExpandida]

  const handleFilialChange = (value: Identificador) => {
    setSelectedFilial(String(value))
  }

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value)
  }

  const handleExpandirLinha = (expanded: boolean, record: AgendaFornecedor) => {
    setAgendaExpandida(expanded ? record.key : null)
  }

  const handleNovaDataProcessada = () => {
    setAgendaExpandida(null)
    void agendamentosQuery.refetch()
  }

  const handleButtonBookingNFDetailsClick = (record: AgendaRecebimentoBookingNFResponse) => {
    setOpenBookingSearch(false)
    const isAgendaNoFiltroAtual = agendamentos.some((agenda) => agenda.key === record.agendaId)

    if (!isAgendaNoFiltroAtual) {
      message.info(
        `Agendamento ${record.agendaId} não está no filtro atual. Ajuste a filial ou o status para localizá-lo.`
      )
      return
    }

    setAgendaExpandida(record.agendaId)
  }

  const handleButtonValidarNovaDataClick = (record: AgendaFornecedor) => {
    setAgendaExpandida((chaveAtual) => (chaveAtual === record.key ? null : record.key))
  }

  const handleMotivoCompletoClick = (chaveAgenda: number) => {
    setMotivosExpandidos((chaves) =>
      chaves.includes(chaveAgenda) ? chaves.filter((chave) => chave !== chaveAgenda) : [...chaves, chaveAgenda]
    )
  }

  const handleButtonFinalizeSchedulerClick = (record: AgendaFornecedor) => {
    definirFilialGlobal(selectedFilial)
    navigate(`/agendamento/finalize/${record.key}`)
  }

  const handleNfGetBooking = () => {
    if (numberNotaFiscal === null || user.code === null) return

    if (notaFiscalPesquisada === numberNotaFiscal) {
      void notasQuery.refetch()
      return
    }

    setNotaFiscalPesquisada(numberNotaFiscal)
  }

  const renderStatusComMotivo = (record: AgendaFornecedor) => (
    <span className={'status-cell-supplier-report-class'}>
      {renderPastilhaStatus(record.status)}
      <MotivoStatus
        record={record}
        isExpandido={motivosExpandidos.includes(record.key)}
        onToggle={handleMotivoCompletoClick}
      />
    </span>
  )

  const renderPreAgendamento = (record: AgendaFornecedor) => {
    const hasPreAgenda = record.hasPreAgenda === true
    const isPreAgendaConcluida = record.hasPreAgendaCompleted === true
    const classePonto = isPreAgendaConcluida
      ? 'pre-cell-supplier-report-class__ponto--concluido'
      : 'pre-cell-supplier-report-class__ponto--pendente'

    return (
      <span className={'pre-cell-supplier-report-class'}>
        <span>{hasPreAgenda ? 'Sim' : 'Não'}</span>
        {hasPreAgenda && (
          <span className={'pre-cell-supplier-report-class__estado'}>
            <span className={`pre-cell-supplier-report-class__ponto ${classePonto}`} aria-hidden={true} />
            {isPreAgendaConcluida ? 'Concluído' : 'A finalizar'}
          </span>
        )}
      </span>
    )
  }

  const renderAcoes = (record: AgendaFornecedor) => {
    const hasPreAgendaPendente = record.hasPreAgenda === true && record.hasPreAgendaCompleted === false
    const hasNovaDataParaValidar = record.status === 'ALTERADO'
    const isLinhaExpandida = record.key === agendaExpandida

    if (!hasPreAgendaPendente && !hasNovaDataParaValidar) {
      return (
        <>
          <span className={'table-supplier-report-class__sem-acao'} aria-hidden={true}>
            —
          </span>
          <span className={'table-supplier-report-class__somente-leitor'}>
            Nenhuma ação disponível para o agendamento {record.key}
          </span>
        </>
      )
    }

    return (
      <Flex vertical={true} gap={6}>
        {hasPreAgendaPendente && (
          <Popconfirm
            title='Tem certeza de que deseja finalizar o cadastro?'
            onConfirm={() => handleButtonFinalizeSchedulerClick(record)}
            okText='Sim'
            cancelText='Não'
          >
            <Button
              size='small'
              block={true}
              icon={<CheckOutlined />}
              className={'botao-supplier-report-class'}
              aria-label={`Finalizar Pré-agendamento do agendamento ${record.key}`}
            >
              Finalizar Pré
            </Button>
          </Popconfirm>
        )}
        {hasNovaDataParaValidar && (
          <Button
            type='dashed'
            size='small'
            block={true}
            icon={<SyncOutlined />}
            className={'botao-supplier-report-class'}
            aria-label={`Validar a nova data de entrega do agendamento ${record.key}`}
            aria-expanded={isLinhaExpandida}
            aria-controls={isLinhaExpandida ? idRegiaoDetalhes(record.key) : undefined}
            onClick={() => handleButtonValidarNovaDataClick(record)}
          >
            Validar nova data
          </Button>
        )}
      </Flex>
    )
  }

  const renderResumoDoFiltro = () => (
    <div className={'table-foot-supplier-report-class'}>
      <span>
        Agendamentos no filtro:{' '}
        <span className={'table-foot-supplier-report-class__valor'}>
          {formatadorDeNumero.format(agendamentos.length)}
        </span>
      </span>
      <span>
        Volumes solicitados no filtro:{' '}
        <span className={'table-foot-supplier-report-class__valor'}>
          {formatadorDeNumero.format(totalVolumesFiltrados)}
        </span>
      </span>
    </div>
  )

  const renderDetalhesAgendamento = (record: AgendaFornecedor) => (
    <DetalhesAgendamentoExpandido record={record} onNovaDataProcessada={handleNovaDataProcessada} />
  )

  const resolverClasseDaLinha = (record: AgendaFornecedor) =>
    record.key === agendaExpandida ? 'table-supplier-report-class__linha--aberta' : ''

  const columns: ColunaTabela<AgendaFornecedor>[] = [
    {
      title: 'ID Agenda',
      dataIndex: 'key',
      key: 'key',
      width: 115,
      fixed: 'left',
      sorter: (a, b) => a.key - b.key,
      sortDirections: ['descend', 'ascend'],
      render: (valor) => <span className={'table-supplier-report-class__id'}>{String(valor)}</span>,
    },
    ...(hasRolePreAgendamento
      ? [
          {
            title: 'Pré Agendamento',
            dataIndex: 'hasPreAgenda',
            key: 'hasPreAgenda',
            width: 145,
            render: (_: unknown, record: AgendaFornecedor) => renderPreAgendamento(record),
          },
        ]
      : []),
    {
      title: 'Transportadora',
      dataIndex: 'transportadora',
      key: 'transportadora',
      width: 250,
      searchable: true,
    },
    {
      title: 'Volumes Solicitados',
      dataIndex: 'totalVolumes',
      key: 'totalVolumes',
      width: 160,
      align: 'right',
      className: 'table-supplier-report-class__numero',
      sorter: (a, b) => a.totalVolumes - b.totalVolumes,
      sortDirections: ['descend', 'ascend'],
      render: (valor) => formatadorDeNumero.format(Number(valor ?? 0)),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 280,
      searchable: true,
      render: (_, record) => renderStatusComMotivo(record),
    },
    {
      title: 'Data Entrega',
      dataIndex: 'dataEntrega',
      key: 'dataEntrega',
      width: 140,
      render(date) {
        return formatDateTime(String(date))
      },
      sorter: (a, b) =>
        dayjs(a.dataEntrega, FORMATO_DATA_ENTREGA).unix() - dayjs(b.dataEntrega, FORMATO_DATA_ENTREGA).unix(),
      sortDirections: ['descend', 'ascend'],
    },
    {
      title: 'Tipo Caminhões',
      dataIndex: 'tipoCaminhoes',
      key: 'tipoCaminhoes',
      width: 150,
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => renderAcoes(record),
    },
  ]

  const handleFecharPesquisaNotaFiscal = () => {
    setOpenBookingSearch(false)
  }

  return (
    <>
      <section className={'report-region-supplier-report-class'} aria-label='Relatório de agendamentos do fornecedor'>
        <div className={'filter-bar-supplier-report-class'} role='search' aria-label='Filtros do relatório'>
          <div className={'filter-bar-supplier-report-class__grupo'}>
            <div className={'filter-field-supplier-report-class'}>
              <label htmlFor='centro-custo-supplier' className={'filter-field-supplier-report-class__rotulo'}>
                Centro De Custo
              </label>
              <Select
                id='centro-custo-supplier'
                placeholder={'Selecione uma Filial:'}
                value={selectedFilial}
                options={filiaisQuery.data ?? []}
                onChange={handleFilialChange}
                className={'filter-field-supplier-report-class__controle'}
                showSearch
                filterOption={(input, option) =>
                  option ? option.label.toLowerCase().includes(input.toLowerCase()) : false
                }
              />
            </div>
            <div className={'filter-field-supplier-report-class'}>
              <label htmlFor='status-supplier' className={'filter-field-supplier-report-class__rotulo'}>
                Status
              </label>
              <Select
                id='status-supplier'
                placeholder={'Selecione um Status:'}
                value={selectedStatus}
                options={STATUS_OPTIONS}
                onChange={handleStatusChange}
                className={'filter-field-supplier-report-class__controle'}
                showSearch
              />
            </div>
          </div>
          {horarioUltimaAtualizacao !== null && (
            <span className={'filter-bar-supplier-report-class__acoes'}>Atualizado às {horarioUltimaAtualizacao}</span>
          )}
        </div>

        <section
          className={'table-card-supplier-report-class'}
          aria-labelledby='titulo-agendamentos-supplier'
          aria-busy={isAtualizandoAgendamentos}
        >
          {isAtualizandoAgendamentos && (
            <div
              className={'table-card-supplier-report-class__progresso'}
              role='progressbar'
              aria-label='Atualizando agendamentos'
            />
          )}
          <div className={'table-card-supplier-report-class__cabecalho'}>
            <div className={'table-card-supplier-report-class__cabecalho-esquerda'}>
              <h2 className={'table-card-supplier-report-class__titulo'} id='titulo-agendamentos-supplier'>
                Agendamentos
              </h2>
              <span className={'table-card-supplier-report-class__dica'}>
                {rotuloFilialSelecionada} · Status {rotuloStatusSelecionado}
              </span>
            </div>
            <span className={'table-card-supplier-report-class__dica'}>
              Expanda uma linha para ver notas e conhecimentos · motivos aparecem na coluna Status
            </span>
          </div>
          <Skeleton
            loading={agendamentosQuery.isPending}
            active
            paragraph={{ rows: 5 }}
            className={'table-card-supplier-report-class__esqueleto'}
          >
            <DefaultTable
              baseColumns={columns}
              dataSource={agendamentos}
              className={'table-supplier-report-class'}
              rowClassName={resolverClasseDaLinha}
              expandable={{
                expandedRowKeys: chavesExpandidas,
                expandedRowRender: renderDetalhesAgendamento,
                onExpand: handleExpandirLinha,
                columnWidth: 44,
                fixed: 'left',
              }}
              footerContent={renderResumoDoFiltro()}
              scroll={{ y: 'min(64vh, 680px)' }}
              pagination={{ pageSize: 10 }}
            />
          </Skeleton>
        </section>
      </section>

      <FloatButton
        icon={<SyncOutlined />}
        tooltip={{ title: 'Atualizar Agendamentos', placement: 'left' }}
        className={'float-button-top-one-class float-button-supplier-report-class'}
        style={{ insetBlockStart: 80, insetBlockEnd: 'auto', insetInlineEnd: 10 }}
        aria-label='Atualizar Agendamentos'
        onClick={() => void agendamentosQuery.refetch()}
      />
      <Popover
        open={openBookingSearch}
        onOpenChange={setOpenBookingSearch}
        trigger='click'
        placement='bottomRight'
        content={
          <PainelPesquisaNotaFiscal
            valor={numberNotaFiscal}
            onChange={setNumberNotaFiscal}
            onBuscar={handleNfGetBooking}
            isFetching={notasQuery.isFetching}
            notas={notasEncontradas}
            hasPesquisa={hasPesquisaPorNotaFiscal}
            onFechar={handleFecharPesquisaNotaFiscal}
            onDetalhes={handleButtonBookingNFDetailsClick}
          />
        }
        classNames={{ root: 'nf-panel-supplier-report-class', container: 'nf-panel-supplier-report-class__container' }}
      >
        <FloatButton
          icon={<SearchOutlined />}
          type={openBookingSearch ? 'primary' : 'default'}
          tooltip={{ title: 'Pesquisar', placement: 'left' }}
          className={'float-button-top-two-class float-button-supplier-report-class'}
          style={{ insetBlockStart: 130, insetBlockEnd: 'auto', insetInlineEnd: 10 }}
          aria-label='Pesquisar'
          aria-expanded={openBookingSearch}
          aria-controls={ID_PAINEL_PESQUISA_NF}
        />
      </Popover>
    </>
  )
}
